import { PermissionFeature, PermissionFeatureToActions, Permissions } from "@/protocols/permission"

import ApiService from "@/services/Api"

export type RoleType = "system" | "custom"

export type Role = {
	id: number
	name: string
	type: RoleType
	description?: string
	permissions: Permissions
	createdAt: Date
	updatedAt: Date
}

export type RoleFormattedForList = Omit<Role, "permissions"> & {
	usersInRoleQuantity: number
	lastUpdatedUserName: string
}

type RetrieveAllRolesResponse = {
	roles: RoleFormattedForList[]
}

type RetrieveRoleResponse = {
	role: Role
}

export type CreateRoleInput = {
	name: string
	description?: string
}

export type UpdateRoleInput = Partial<CreateRoleInput> & {
	permissions?: Permissions
}

export type CreateRoleResponse = {
	id: number
}

class RoleService {
	async retrieveAll (): Promise<RoleFormattedForList[]> {
		const { data } = await ApiService.get<RetrieveAllRolesResponse>("/role")

		return data.roles
	}

	async retrieveById (id: number): Promise<Role> {
		const { data } = await ApiService.get<RetrieveRoleResponse>(`/role/${id}`)

		return data.role
	}

	async create (input: CreateRoleInput): Promise<number> {
		const { data } = await ApiService.post<CreateRoleResponse>("/role", input)

		return data.id
	}

	async update (id: number, input: UpdateRoleInput): Promise<void> {
		await ApiService.put(`/role/${id}`, input)
	}

	async delete (id: number): Promise<void> {
		await ApiService.delete(`/role/${id}`)
	}

	async checkPermission (feature: PermissionFeature, action: PermissionFeatureToActions[PermissionFeature]): Promise<boolean> {
		const { data } = await ApiService.get("/role/check-permission", {
			params: {
				feature,
				action
			}
		})

		return data.hasPermission
	}
}

export default new RoleService()
